import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Separator } from '../ui/separator';
import { 
  Lock, Sparkles, CheckCircle2, XCircle, ArrowRight, 
  Calendar, TrendingUp, CreditCard, Loader2, AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Module display names
const MODULE_NAMES = {
  practice_sessions: 'Practice Sessions',
  progress_tracker: 'Progress Tracker',
  notebook: 'Study Notebook',
  exam_insights: 'Exam Insights'
};

/**
 * Modal showing upgrade options for users who want to unlock specific modules.
 * Shows pro-rata pricing and eligible plans.
 */
const UpgradeOptionsModal = ({ 
  open, 
  onClose, 
  targetModule = null,  // The module user wants to unlock (e.g., 'exam_insights')
  featureName = 'this feature'  // Display name for the feature
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upgradeData, setUpgradeData] = useState(null);

  useEffect(() => {
    if (open) {
      fetchUpgradeOptions();
    }
  }, [open, targetModule]);

  const fetchUpgradeOptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = targetModule ? `?target_module=${targetModule}` : '';
      const response = await axios.get(`${API}/user/upgrade-options${params}`);
      setUpgradeData(response.data);
    } catch (err) {
      console.error('Failed to fetch upgrade options:', err);
      setError(err.response?.data?.detail || 'Failed to load upgrade options');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (plan) => {
    // Navigate directly to checkout with pro-rata upgrade data
    navigate('/checkout', {
      state: {
        plan: {
          id: plan.id,
          name: plan.name,
          description: plan.description,
          price: plan.price,
          duration_days: plan.duration_days,
          features: plan.features || [],
          module_access: plan.module_access
        },
        isUpgrade: true,
        isProRataUpgrade: true,
        rebateAmount: upgradeData?.current_plan?.rebate_amount || 0,
        currentPlanName: upgradeData?.current_plan?.name,
        daysRemaining: upgradeData?.current_plan?.days_remaining || 0,
        upgradeCost: plan.upgrade_cost
      }
    });
    onClose();
  };

  const formatDuration = (days) => {
    if (days === 30) return '1 Month';
    if (days === 90) return '3 Months';
    if (days === 180) return '6 Months';
    if (days === 270) return '9 Months';
    if (days === 365) return '1 Year';
    return `${days} Days`;
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-blue-600" />
            Unlock {featureName}
          </DialogTitle>
          <DialogDescription>
            {targetModule 
              ? `Your current plan doesn't include ${featureName}. Upgrade to a plan that includes this feature.`
              : 'Choose a plan with more features to unlock additional modules.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Loading upgrade options...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
              <p className="text-red-600">{error}</p>
              <Button variant="outline" onClick={fetchUpgradeOptions} className="mt-4">
                Try Again
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Current Plan Summary */}
              {upgradeData?.current_plan && (
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Your Current Plan</h4>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{upgradeData.current_plan.name}</p>
                      <p className="text-sm text-gray-600">
                        {formatDuration(upgradeData.current_plan.duration_days)} • 
                        {formatPrice(upgradeData.current_plan.daily_rate)}/day
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        {upgradeData.current_plan.days_remaining} days remaining
                      </p>
                      {upgradeData.current_plan.rebate_amount > 0 && (
                        <p className="text-sm text-green-600 font-medium">
                          <TrendingUp className="w-4 h-4 inline mr-1" />
                          {formatPrice(upgradeData.current_plan.rebate_amount)} credit
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Current module access */}
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-gray-500 mb-2">Current Modules:</p>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(upgradeData.current_plan.module_access || {}).map(([key, value]) => (
                        <Badge 
                          key={key}
                          variant="outline"
                          className={`text-xs ${value ? 'bg-green-50 text-green-700 border-green-300' : 'bg-gray-100 text-gray-400 border-gray-300'}`}
                        >
                          {value ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                          {MODULE_NAMES[key] || key}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Eligible Upgrade Plans */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">
                  Available Upgrades
                  {upgradeData?.eligible_plans?.length > 0 && (
                    <span className="font-normal text-gray-500 ml-2">
                      ({upgradeData.eligible_plans.length} plan{upgradeData.eligible_plans.length > 1 ? 's' : ''})
                    </span>
                  )}
                </h4>
                
                {upgradeData?.eligible_plans?.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <AlertCircle className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">No upgrade plans available for your current duration.</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Check the <Button variant="link" className="px-1 h-auto" onClick={() => { navigate('/plans'); onClose(); }}>Plans page</Button> for other options.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {upgradeData?.eligible_plans?.map((plan) => (
                      <Card 
                        key={plan.id} 
                        className={`cursor-pointer hover:border-blue-400 transition-colors ${plan.is_best_value ? 'border-yellow-400 bg-yellow-50/50' : ''}`}
                        onClick={() => handleSelectPlan(plan)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h5 className="font-semibold text-gray-900">{plan.name}</h5>
                                {plan.is_best_value && (
                                  <Badge className="bg-yellow-500 text-white text-xs">Best Value</Badge>
                                )}
                                <Badge variant="outline" className="text-xs">
                                  {formatDuration(plan.duration_days)}
                                </Badge>
                              </div>
                              {plan.description && (
                                <p className="text-sm text-gray-600 mb-2">{plan.description}</p>
                              )}
                              
                              {/* Module access for this plan */}
                              <div className="flex flex-wrap gap-1 mt-2">
                                {Object.entries(plan.module_access || {}).map(([key, value]) => (
                                  <Badge 
                                    key={key}
                                    variant="outline"
                                    className={`text-xs ${
                                      value 
                                        ? (upgradeData?.current_plan?.module_access?.[key] 
                                            ? 'bg-green-50 text-green-700 border-green-300' 
                                            : 'bg-blue-50 text-blue-700 border-blue-300 ring-1 ring-blue-400')
                                        : 'bg-gray-100 text-gray-400 border-gray-300'
                                    }`}
                                  >
                                    {value ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                                    {MODULE_NAMES[key] || key}
                                    {value && !upgradeData?.current_plan?.module_access?.[key] && (
                                      <span className="ml-1 text-blue-600">NEW</span>
                                    )}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            
                            {/* Pricing */}
                            <div className="text-right ml-4 flex-shrink-0">
                              <div className="text-lg font-bold text-gray-900">
                                {formatPrice(plan.upgrade_cost)}
                              </div>
                              {upgradeData?.current_plan?.rebate_amount > 0 && plan.upgrade_cost < plan.price && (
                                <div className="text-xs text-gray-500 line-through">
                                  {formatPrice(plan.price)}
                                </div>
                              )}
                              <div className="text-xs text-gray-500">
                                {formatPrice(plan.daily_rate)}/day
                              </div>
                              <Button 
                                size="sm" 
                                className="mt-2 bg-blue-600 hover:bg-blue-700"
                                onClick={(e) => { e.stopPropagation(); handleSelectPlan(plan); }}
                              >
                                Select <ArrowRight className="w-4 h-4 ml-1" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Pricing Explanation */}
              {upgradeData?.current_plan?.rebate_amount > 0 && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <CreditCard className="w-4 h-4 inline mr-1" />
                    <strong>Pro-rata Pricing:</strong> You have {formatPrice(upgradeData.current_plan.rebate_amount)} credit 
                    from your current plan ({upgradeData.current_plan.days_remaining} unused days). 
                    This will be applied to your upgrade.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="pt-4 border-t flex justify-between items-center">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="outline" onClick={() => { navigate('/plans'); onClose(); }}>
            View All Plans
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeOptionsModal;
